import {Button, Card, Flag} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {
  useGetBusinessCaseQuery,
  type DeclineOfferRequestBody,
  type OfferPriceResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {getBusinessCaseStateFlag} from '@dms/shared';

import {composePath} from 'shared/web';

import {isAuctionAwarded, isOfferRejected} from '../../model/auctionView';
import type {AuctionMoney, AuctionOffer, AuctionView} from '../../model/auctionView.types';
import {DeclineOfferDialog} from '../components/DeclineOfferDialog';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {purchaseCaseOwner, type PurchaseCase} from '../components/usePurchaseCase';
import {
  ConfirmMaxPriceButton,
  Countdown,
  PartyHeading,
  PriceBlock,
  StatusBlock,
} from './MaxPriceCalculation';

interface PurchaseSellerCardProps {
  view: AuctionView;
  purchaseCase: PurchaseCase;
  buyer: AuctionOffer | null;
  now: Date;
  maxBuyingPrice: AuctionMoney | null;
  isBrokerage: boolean;
  onConfirmMaxPrice: () => void;
  onDeclineOffer: (body: DeclineOfferRequestBody) => Promise<boolean>;
}

export function PurchaseSellerCard(props: PurchaseSellerCardProps) {
  const format = useAuctionFormat();
  const now = props.now;
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const isAwarded = isAuctionAwarded(props.view);
  const isBuyerRejected = isOfferRejected(props.buyer);
  // v11 records the decline on the purchase case. DMS records it here, next to the selected offer.
  const canDecline =
    props.buyer !== null &&
    !isBuyerRejected &&
    !isAwarded &&
    props.view.allowedActions.includes('declineOffer');
  // The BE blocks confirmWinner with PURCHASE_NOT_SIGNED until the purchase contract is signed.
  const contractBlocker = props.view.blockers.find(({code}) => code === 'PURCHASE_NOT_SIGNED');
  const contractSignedAt = props.view.purchaseContractSignedAt;
  const isContractSigned = contractSignedAt !== null;
  const vehiclePurchase = props.purchaseCase.vehiclesPurchase[0] ?? null;
  const stateFlag = getBusinessCaseStateFlag(props.purchaseCase.businessCaseState);
  const {data: purchaseCaseDetail} = useGetBusinessCaseQuery({
    businessCaseId: props.purchaseCase.id,
  });
  const offerValidTo = purchaseCaseDetail?.offerValidTo ?? null;
  const maxBuyingPrice = props.maxBuyingPrice;
  const isBrokerage = props.isBrokerage;

  const priceValue = (price: OfferPriceResponseBody | null | undefined) => {
    const money = price?.withVat ?? price?.withoutVat;
    return money ? format.money(money) : null;
  };
  const priceTax = (price: OfferPriceResponseBody | null | undefined) =>
    price?.withVat && price.withoutVat && price.withVat.amount !== price.withoutVat.amount
      ? `${format.money(price.withoutVat)} ${i18n.t('general.labels.withoutVat')}`
      : i18n.t('entity.auction.confirmation.vatNotDeductible');

  return (
    <Card variant="inlineWhite" data-testid="auction-confirmation-seller">
      <VStack spacing={4}>
        <PartyHeading
          icon="social/person"
          role={i18n.t('entity.auction.confirmation.sellerOriginalCustomer')}
          name={props.purchaseCase.customerName ?? '—'}
          meta={[props.purchaseCase.code, purchaseCaseOwner(props.purchaseCase)]
            .filter(Boolean)
            .join(' · ')}
          flag={<Show when={stateFlag}>{(flag) => <Flag {...flag} size="small" />}</Show>}
        />
        <Box padding={4} borderRadius="small" backgroundColor="palettes.neutral.10.100">
          <HStack spacing={4} justify="space-between" wrap>
            <PriceBlock
              label={i18n.t('entity.auction.confirmation.buyingPrice')}
              value={priceValue(vehiclePurchase?.purchasePrice)}
              tax={priceTax(vehiclePurchase?.purchasePrice)}
              testId="auction-confirmation-buying-price"
              isLarge
            />
            <Show when={offerValidTo}>
              {(until) => (
                <Countdown
                  until={until}
                  now={now}
                  caption={i18n.t('entity.auction.confirmation.offerValidUntil', {
                    date: format.date(until),
                  })}
                  overdueLabel={i18n.t('entity.auction.confirmation.offerExpired')}
                  testId="auction-confirmation-offer-validity"
                />
              )}
            </Show>
          </HStack>
        </Box>
        <Grid columns={2} spacing={4}>
          <PriceBlock
            label={i18n.t('entity.businessCase.labels.maxBuyingPrice')}
            value={maxBuyingPrice ? format.money(maxBuyingPrice) : null}
            tax={priceTax(vehiclePurchase?.maxBuyingPrice)}
            testId="auction-confirmation-max-buying-price"
          />
          {/* DMS has no separate price consent. The signed purchase document confirms it. */}
          <StatusBlock
            label={i18n.t('entity.auction.confirmation.customerAgreed')}
            value={i18n.t(
              isContractSigned
                ? 'entity.auction.confirmation.priceAgreed'
                : 'entity.auction.confirmation.notConfirmed'
            )}
            note={i18n.t(
              isContractSigned
                ? 'entity.auction.confirmation.confirmedBySignedDocument'
                : 'entity.auction.confirmation.confirmInPurchasePricing'
            )}
            testId="auction-confirmation-price-agreed"
          />
        </Grid>
        {/* DMS has no purchase contract number. The PO uses the business case code instead. */}
        <StatusBlock
          label={i18n.t('entity.auction.steps.tradeInAgreement')}
          value={i18n.t(
            isContractSigned
              ? 'entity.auction.confirmation.signed'
              : 'entity.auction.confirmation.notSigned'
          )}
          note={
            contractSignedAt
              ? [props.purchaseCase.code, format.dateTime(contractSignedAt)]
                  .filter(Boolean)
                  .join(' · ')
              : (contractBlocker?.message ??
                i18n.t('entity.auction.confirmation.managedInPurchaseCase', {
                  code: props.purchaseCase.code ?? '',
                }))
          }
          testId="auction-confirmation-contract"
        />
        <Show when={props.purchaseCase}>
          {(purchaseCase) => (
            <Box borderTop="1px solid" borderColor="general.separator" paddingTop={4}>
              <HStack spacing={4} align="center" justify="space-between" wrap>
                <HStack spacing={4} align="center" wrap>
                  <ConfirmMaxPriceButton
                    purchaseCaseId={purchaseCase.id}
                    isBrokerage={isBrokerage}
                    onConfirm={() => props.onConfirmMaxPrice()}
                  />
                  <Button
                    variant="link"
                    title={i18n.t('entity.auction.confirmation.openPurchaseCase')}
                    to={composePath(businessCaseRoutes.overview, {
                      params: {id: purchaseCase.id},
                      isSearchParamsPreserved: false,
                    })}
                    data-testid="auction-confirmation-open-purchase"
                  />
                  {/* The BE marks the contract signed when the checkout closes the contract. */}
                  <Show when={!isContractSigned}>
                    <Button
                      variant="link"
                      title={i18n.t('entity.auction.confirmation.signPurchaseContract', {
                        defaultValue: 'Sign the purchase contract',
                      })}
                      to={composePath(businessCaseRoutes.checkout, {
                        params: {id: purchaseCase.id},
                        isSearchParamsPreserved: false,
                      })}
                      data-testid="auction-confirmation-sign-contract"
                    />
                  </Show>
                </HStack>
                <Show when={canDecline}>
                  <Button
                    variant="dangerOutlined"
                    leftIcon="navigation/close"
                    title={i18n.t('entity.auction.decline.title')}
                    onClick={() => setIsDeclineOpen(true)}
                    data-testid="auction-record-decline"
                  />
                </Show>
              </HStack>
            </Box>
          )}
        </Show>
        <Show when={props.buyer}>
          {(buyer) => (
            <DeclineOfferDialog
              isOpen={isDeclineOpen}
              offerId={buyer.offerId}
              traderName={buyer.traderName}
              onClose={() => setIsDeclineOpen(false)}
              onSave={props.onDeclineOffer}
            />
          )}
        </Show>
      </VStack>
    </Card>
  );
}
